<script lang="ts" setup>
import {toRef} from "vue";
import {useField} from "vee-validate";

const props = withDefaults(
  defineProps<{
    id?: string;
    name: string;
    label: string;
    value?: boolean;
    asRequired?: boolean;
    successMessage?: string;
    errorMessage?: string;
    rules?: string;
  }>(),
  {
    value: true,
  },
);

const name = toRef(props, "name");

const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
  checked,
  errors,
  meta,
} = useField(name, props.rules, {
  type: "checkbox",
  checkedValue: props.value,
});

const {t} = useI18n();

const reinterpolate = (errorMessage: string, named: Record<string, any>) => {
  const asResolved = t(errorMessage, named, {resolvedMessage: true});
  if (asResolved !== errorMessage) {
    return asResolved;
  }
  return t(errorMessage, named, {resolvedMessage: false});
};
</script>

<template>
  <div
    :class="{'has-error': !!errorMessage, success: meta.valid}"
    class="checkbox-input">
    <div class="checkbox-container">
      <input
        type="checkbox"
        :id="id ? id : name"
        :name="name"
        :checked="checked"
        :value
        @blur="handleBlur"
        @change="handleChange"
      />
      <label :for="id ? id : name">
        {{ label }} <sup v-if="asRequired">*</sup>
      </label>
    </div>
    <div
      v-if="errorMessage"
      class="help-message mt-1 text-sunrise-100 page-light:text-sunrise-200"
      role="alert">
      {{ reinterpolate(errorMessage, {label, name, inputValue}) }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.checkbox-input {
  display: flex;
  flex-direction: column;

  .checkbox-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  label {
    font-weight: normal;
    sup {
      color: var(--color-accent);
    }
  }

  [role="alert"] {
    margin-left: calc(var(--spacing) / 2);
  }

  .help-message {
    font-size: 0.75em;
  }

  input[type="checkbox"] {
    appearance: none;
    width: 1.25rem;
    height: 1.25rem;
    background-color: rgba(255, 255, 255, 0);
    border-radius: 0;
    @apply border;
    position: relative;
    cursor: pointer;

    &:checked {
      background-color: var(--color-accent);
      border-color: var(--color-accent);

      &::after {
        content: "";
        position: absolute;
        left: 0.3rem;
        top: 0.1rem;
        width: 0.4rem;
        height: 0.7rem;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    }

    &:focus-visible {
      @apply page-dark:outline-white page-light:outline-black;
      outline-style: solid;
      outline-width: 2px;
    }
  }
}
</style>
